import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { API_URL } from '../config/api';

export default function WebhooksPage() {
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', events: [], secret: '', api_key: '', enabled: true });
  const [msg, setMsg] = useState('');

  const fetchAll = async () => {
    const [subsRes, eventsRes, delRes] = await Promise.all([
      axios.get(`${API_URL}/api/webhook-subscriptions`, { withCredentials: true }),
      axios.get(`${API_URL}/api/webhook-subscriptions/events`, { withCredentials: true }),
      axios.get(`${API_URL}/api/webhook-subscriptions/deliveries?limit=20`, { withCredentials: true }),
    ]);
    setSubs(subsRes.data);
    setEvents(eventsRes.data.events);
    setDeliveries(delRes.data);
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => {
    setForm({ name: '', url: '', events: [], secret: '', api_key: '', enabled: true });
    setEditing({});
  };

  const openEdit = (sub) => {
    setEditing(sub);
    setForm({ name: sub.name, url: sub.url, events: sub.events || [], secret: '', api_key: sub.api_key || '', enabled: sub.enabled });
  };

  const save = async () => {
    if (!form.url || !form.events.length) { setMsg('URL y al menos 1 evento son requeridos'); return; }
    try {
      if (editing && editing.id) {
        await axios.put(`${API_URL}/api/webhook-subscriptions/${editing.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/webhook-subscriptions`, form, { withCredentials: true });
      }
      setMsg('Suscripcion guardada');
      setEditing(null);
      fetchAll();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al guardar');
    }
    setTimeout(() => setMsg(''), 4000);
  };

  const remove = async (sub) => {
    if (!window.confirm(`Eliminar suscripcion "${sub.name}"?`)) return;
    await axios.delete(`${API_URL}/api/webhook-subscriptions/${sub.id}`, { withCredentials: true });
    fetchAll();
  };

  const testHook = async (sub) => {
    try {
      await axios.post(`${API_URL}/api/webhook-subscriptions/${sub.id}/test`, {}, { withCredentials: true });
      setMsg(`Test enviado a ${sub.url}`);
      setTimeout(() => { fetchAll(); setMsg(''); }, 3000);
    } catch (e) {
      setMsg('Error enviando test');
    }
  };

  const toggleEvent = (key) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(key) ? f.events.filter(e => e !== key) : [...f.events, key]
    }));
  };

  return (
    <div data-testid="webhooks-page" className="space-y-6">
      <div className="flex justify-between items-center border-b border-slate-700 pb-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Webhooks Salientes</h1>
          <p className="text-slate-300 text-sm mt-1">Notifica automaticamente a otras apps (Estimados, etc.) cuando ocurren eventos en el CRM</p>
        </div>
        <Button data-testid="new-sub-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700">+ Nueva Suscripcion</Button>
      </div>

      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {subs.length === 0 && <p className="text-slate-400 col-span-2 text-center py-8">Sin webhooks configurados. Crea uno para empezar.</p>}
        {subs.map(s => (
          <Card key={s.id} className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-white text-base">{s.name}</CardTitle>
                  <p className="text-blue-400 text-xs font-mono mt-1 break-all">{s.url}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${s.enabled ? 'bg-green-900/40 text-green-300' : 'bg-slate-700 text-slate-400'}`}>
                  {s.enabled ? 'Activo' : 'Pausado'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1 mb-3">
                {(s.events || []).map(e => <span key={e} className="text-xs bg-slate-700 text-slate-200 px-2 py-0.5 rounded">{e}</span>)}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => testHook(s)}>Test Ping</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(s)}>Editar</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(s)}>Eliminar</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {deliveries.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader><CardTitle className="text-white text-base">Entregas Recientes</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {deliveries.map(d => (
                <div key={d.id} className="flex items-center gap-3 text-xs font-mono text-slate-300">
                  <span className={d.delivered ? 'text-green-400' : 'text-red-400'}>{d.delivered ? 'OK' : 'FAIL'}</span>
                  <span>{d.status_code || '-'}</span>
                  <span className="text-slate-400">{d.event}</span>
                  <span className="truncate flex-1 text-slate-500">{d.url}</span>
                  <span className="text-slate-500">{new Date(d.created_at).toLocaleString('es-ES')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <Card className="bg-slate-800 border-slate-700 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader><CardTitle className="text-white">{editing.id ? 'Editar' : 'Nueva'} Suscripcion</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-slate-300 text-xs mb-1 block">Nombre</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Estimados App" />
              </div>
              <div>
                <label className="text-slate-300 text-xs mb-1 block">URL Destino</label>
                <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://growth-platform-pro.preview.emergentagent.com/api/webhook" />
              </div>
              <div>
                <label className="text-slate-300 text-xs mb-1 block">Eventos a enviar</label>
                <div className="grid grid-cols-1 gap-1 max-h-60 overflow-y-auto bg-slate-900 rounded p-2">
                  {events.map(e => (
                    <label key={e.key} className="flex items-center gap-2 text-sm text-slate-200 p-1 hover:bg-slate-800 rounded cursor-pointer">
                      <input type="checkbox" checked={form.events.includes(e.key)} onChange={() => toggleEvent(e.key)} />
                      <span className="font-mono text-xs text-blue-300">{e.key}</span>
                      <span className="text-xs text-slate-400">{e.desc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-slate-300 text-xs mb-1 block">Secret HMAC (opcional, para firmar payloads)</label>
                <Input value={form.secret} onChange={e => setForm({ ...form, secret: e.target.value })} placeholder="Dejar vacio para no firmar" />
              </div>
              <div>
                <label className="text-slate-300 text-xs mb-1 block">API Key header extra (opcional)</label>
                <Input value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder="Se enviara como X-API-Key" />
              </div>
              <label className="flex items-center gap-2 text-slate-200 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} /> Activo
              </label>
              <div className="flex gap-2">
                <Button onClick={save} className="flex-1 bg-blue-600 hover:bg-blue-700">Guardar</Button>
                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
